import type {OlHTMLAttributes} from 'react';

import styled from '@emotion/styled';
import {observer} from 'mobx-react-lite';

import type {ThemeColor} from '@nfq/colors';
import type {WithChildren} from 'types/global';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface ComponentProps {
    /**
     * Optional. Specifies the color of the wrapper, which can be used for text or background color based on CSS setup.
     * The color is selected from the `colors` object in the `DefaultTheme`, ensuring consistency with the theme.
     *
     * @default 'primaryFontColor'
     */
    color?: ThemeColor;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'NumberedList'
     */
    testId?: string;
    /**
     * The type of the numbered list, which controls how the list items are numbered.
     * This prop corresponds to the `type` attribute of the `<ol>` element.
     */
    type: OlHTMLAttributes<'web'>['type'];
}

/**
 * Renders a generic `NumberedList` wrapper component that can be styled dynamically based on provided props.
 * This component is designed to encapsulate children elements, potentially serving as a container for a list of items.
 * The styling of the `NumberedList` can react to theme changes if `$reactsToTheme` is set to true, allowing the
 * component's color to adapt based on the current theme. The `type` prop controls how the list items are numbered.
 * This component is highly flexible and can be used for various use cases, such as displaying a list of ordered items
 * in a web application. It supports theme-based styling and can wrap any child components.
 *
 * @param props          The component props.
 * @param props.color    Optional. Specifies the color of the wrapper, which can be used for text or background color based on CSS setup.
 * @param props.children Child components or elements to be wrapped by this component.
 * @param props.testId   A unique identifier for the component, useful for testing purposes.
 * @param props.type     The type of the list, determining how the list items are numbered.
 * @returns A React component that serves as a flexible wrapper for numbered list-like content, with optional theme-reactive styling.
 *
 * @example
 * ```tsx
 * <NumberedList $color="primary" $reactsToTheme={true} testId="unique-list" type="1">
 *   <Item>Item 1</Item>
 *   <Item>Item 2</Item>
 * </NumberedList>
 * ```
 */
const NumberedList = observer(({children, color, testId = 'NumberedList', type}: WithChildren<ComponentProps>) => (
    <Wrapper $color={color} data-cy={testId} type={type}>{children}</Wrapper>
));

NumberedList.displayName = 'NumberedList';

export {NumberedList};

interface WrapperProps {
    $color?: ThemeColor;
}

const Wrapper = styled.ol<WrapperProps>`
    color: ${({$color}) => ($color ?? null)};
    margin: 0;
    padding-left: 2.8rem;

    li {
        padding-left: 2.8rem;
        position: relative;

        &:not(:last-child) {
            margin-bottom: 1rem;
        }
    }
`;